import React, { useEffect, useMemo, useState } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import { getEpisodes } from "./api";

function formatDate(value) {
    if (!value) return "";
    const text = String(value).slice(0, 10);
    const dt = new Date(`${text}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return String(value);
    return dt.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
}

function toLocalYmd(dateObj) {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function formatPublishDateLabel(value) {
    if (!value) return "";
    const text = String(value).slice(0, 10);
    const dt = new Date(`${text}T00:00:00`);
    if (Number.isNaN(dt.getTime())) return String(value);

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (text === toLocalYmd(today)) return "Today";
    if (text === toLocalYmd(tomorrow)) return "Tomorrow";
    return formatDate(text);
}

function isDistributionEnabled(value) {
    if (value === null || value === undefined || value === "") return true;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value).trim().toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
    return true;
}

function formatEpisodeCodeLabel(seasonNumber, episodeNumber) {
    const episode = Number.parseInt(episodeNumber, 10);
    if (!Number.isFinite(episode) || episode <= 0) return "";

    const season = Number.parseInt(seasonNumber, 10);
    if (Number.isFinite(season) && season > 0) {
        return `S${season}E${episode}`;
    }

    return `E${episode}`;
}

function getStatusColor(status) {
    const s = String(status || "").toLowerCase();
    switch (s) {
        case "pending approval":
            return { bg: "#fef9c3", text: "#a16207" };
        case "scheduled":
            return { bg: "#dcfce7", text: "#166534" };
        case "published":
            return { bg: "#bbf7d0", text: "#15803d" };
        case "approved":
            return { bg: "#d9f99d", text: "#4d7c0f" };
        case "rejected":
            return { bg: "#fee2e2", text: "#b91c1c" };
        default:
            return { bg: "#e5e7eb", text: "#374151" };
    }
}

function shouldShowPublishesForStatus(status) {
    const normalized = String(status || "").toLowerCase().trim();
    return ["pending approval", "approved", "scheduled", "published"].includes(
        normalized
    );
}

function canReviewEpisode(ep) {
    return (
        String(ep?.status || "").toLowerCase().trim() === "pending approval" &&
        !!String(ep?.review_token || "").trim() &&
        Number(ep?.can_review_episodes ?? 1) === 1
    );
}

function ClientDashboard({ onLogout, embedded = false }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [episodes, setEpisodes] = useState([]);

    useEffect(() => {
        async function load() {
            setLoading(true);
            setError("");
            try {
                const data = await getEpisodes();
                setEpisodes(Array.isArray(data?.episodes) ? data.episodes : []);
            } catch (err) {
                console.error(err);
                setError(err.message || "Failed to load dashboard");
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    const stats = useMemo(() => {
        const podcastNames = new Set(
            episodes
                .map((ep) => ep?.podcast_name || ep?.podcast || "")
                .filter(Boolean)
        );
        const awaitingReview = episodes.filter(
            (ep) =>
                String(ep?.status || "").toLowerCase() === "pending approval" &&
                Number(ep?.can_review_episodes ?? 1) === 1
        ).length;
        const published = episodes.filter(
            (ep) => String(ep?.status || "").toLowerCase() === "published"
        ).length;

        return {
            podcasts: podcastNames.size,
            episodes: episodes.length,
            awaitingReview,
            published,
        };
    }, [episodes]);

    const recentEpisodes = useMemo(() => {
        const sorted = [...episodes].sort((a, b) => {
            const da = Date.parse(String(a?.publication_date || ""));
            const db = Date.parse(String(b?.publication_date || ""));
            const safeDa = Number.isNaN(da) ? 0 : da;
            const safeDb = Number.isNaN(db) ? 0 : db;
            return safeDb - safeDa;
        });
        return sorted.slice(0, 12);
    }, [episodes]);

    const pendingApprovals = useMemo(() => {
        return episodes
            .filter((ep) => {
                const status = String(ep?.status || "").toLowerCase().trim();
                return (
                    status === "pending approval" &&
                    !!String(ep?.review_token || "").trim() &&
                    Number(ep?.can_review_episodes ?? 1) === 1
                );
            })
            .sort((a, b) => {
                const da = Date.parse(String(a?.publication_date || ""));
                const db = Date.parse(String(b?.publication_date || ""));
                const safeDa = Number.isNaN(da) ? Number.MAX_SAFE_INTEGER : da;
                const safeDb = Number.isNaN(db) ? Number.MAX_SAFE_INTEGER : db;
                return safeDa - safeDb;
            });
    }, [episodes]);

    function openEpisodeInEpisodesView(episodeId) {
        const id = String(episodeId || "").trim();
        if (!id) return;
        window.location.hash = `#/episodes?id=${encodeURIComponent(id)}`;
    }

    function openReviewFromToken(token) {
        const safe = String(token || "").trim();
        if (!safe) return;
        window.open(
            `#/review?token=${encodeURIComponent(safe)}`,
            "_blank",
            "noopener,noreferrer"
        );
    }

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                <div className="portal-page__header">
                    <h2>Your Dashboard</h2>
                    <p>Overview of your podcasts and episodes.</p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 12,
                    }}
                >
                    <div className="card">
                        <div className="form-field__label">Podcasts</div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.podcasts}</div>
                    </div>
                    <div className="card">
                        <div className="form-field__label">Episodes</div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.episodes}</div>
                    </div>
                    <div className="card">
                        <div className="form-field__label">Awaiting Review</div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>
                            {stats.awaitingReview}
                        </div>
                    </div>
                    <div className="card">
                        <div className="form-field__label">Published</div>
                        <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.published}</div>
                    </div>
                </div>

                {error && <div className="alert alert--error">{error}</div>}
                {loading && <p>Loading dashboard…</p>}

                {!loading && !error && (
                    <>
                        <div className="card" style={{ marginTop: 12 }}>
                            <h3 style={{ marginTop: 0 }}>Pending Approval</h3>
                            {pendingApprovals.length === 0 ? (
                                <p>No episodes are currently awaiting your review.</p>
                            ) : (
                                <div className="episode-list">
                                    {pendingApprovals.map((ep) => (
                                        <div
                                            key={`pending-${ep.id}`}
                                            className="episode-card"
                                            style={{ borderLeftColor: "#16a34a", cursor: "pointer" }}
                                            onClick={() => openReviewFromToken(ep.review_token)}
                                        >
                                            <div className="episode-card__main">
                                                <h3 className="episode-card__title">
                                                    {ep.title || "Untitled"}
                                                </h3>
                                                <div className="episode-card__podcast">
                                                    {(() => {
                                                        const podcastName = String(
                                                            ep.podcast_name || ep.podcast || ""
                                                        ).trim();
                                                        const episodeCode = formatEpisodeCodeLabel(
                                                            ep.season_number,
                                                            ep.episode_number
                                                        );
                                                        if (!podcastName) return "No podcast";
                                                        return episodeCode
                                                            ? `${podcastName} · ${episodeCode}`
                                                            : podcastName;
                                                    })()}
                                                </div>
                                                {shouldShowPublishesForStatus(ep?.status) &&
                                                isDistributionEnabled(
                                                    ep?.podcast_distribution
                                                ) &&
                                                formatPublishDateLabel(ep.publication_date) ? (
                                                    <div className="episode-card__meta">
                                                        Publish date: {formatPublishDateLabel(
                                                            ep.publication_date
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="episode-card__meta"
                                                        style={{ visibility: "hidden" }}
                                                        aria-hidden="true"
                                                    >
                                                        Publish date: 00 Jan 0000
                                                    </div>
                                                )}
                                            </div>
                                            <div className="episode-card__right">
                                                <a
                                                    className="episode-card__status-pill"
                                                    href={`#/review?token=${encodeURIComponent(
                                                        ep.review_token
                                                    )}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    style={{
                                                        background: "#dcfce7",
                                                        color: "#166534",
                                                        textDecoration: "none",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    Episode ready for review
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="card" style={{ marginTop: 12 }}>
                            <h3 style={{ marginTop: 0 }}>Recent Episodes</h3>
                            {recentEpisodes.length === 0 ? (
                                <p>No episodes found.</p>
                            ) : (
                                <div className="episode-list">
                                    {recentEpisodes.map((ep) => {
                                        const statusColors = getStatusColor(ep?.status);
                                        const showReadyForReview = canReviewEpisode(ep);
                                        return (
                                            <div
                                                key={ep.id}
                                                className="episode-card"
                                                style={{
                                                    borderLeftColor: "#2563eb",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => {
                                                    if (showReadyForReview) {
                                                        openReviewFromToken(ep.review_token);
                                                        return;
                                                    }
                                                    openEpisodeInEpisodesView(ep.id);
                                                }}
                                            >
                                                <div className="episode-card__main">
                                                    <h3 className="episode-card__title">
                                                        {ep.title || "Untitled"}
                                                    </h3>
                                                    <div className="episode-card__podcast">
                                                        {(() => {
                                                            const podcastName = String(
                                                                ep.podcast_name || ep.podcast || ""
                                                            ).trim();
                                                            const episodeCode = formatEpisodeCodeLabel(
                                                                ep.season_number,
                                                                ep.episode_number
                                                            );
                                                            if (!podcastName) return "No podcast";
                                                            return episodeCode
                                                                ? `${podcastName} · ${episodeCode}`
                                                                : podcastName;
                                                        })()}
                                                    </div>
                                                    {shouldShowPublishesForStatus(ep?.status) &&
                                                    isDistributionEnabled(
                                                        ep?.podcast_distribution
                                                    ) &&
                                                    formatPublishDateLabel(ep.publication_date) ? (
                                                        <div className="episode-card__meta">
                                                            Publish date: {formatPublishDateLabel(
                                                                ep.publication_date
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="episode-card__meta"
                                                            style={{ visibility: "hidden" }}
                                                            aria-hidden="true"
                                                        >
                                                            Publish date: 00 Jan 0000
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="episode-card__right">
                                                    {showReadyForReview ? (
                                                        <a
                                                            className="episode-card__status-pill"
                                                            href={`#/review?token=${encodeURIComponent(
                                                                ep.review_token
                                                            )}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{
                                                                background: "#dcfce7",
                                                                color: "#166534",
                                                                textDecoration: "none",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            Episode ready for review
                                                        </a>
                                                    ) : (
                                                        <span
                                                            className="episode-card__status-pill"
                                                            style={{
                                                                background: statusColors.bg,
                                                                color: statusColors.text,
                                                            }}
                                                        >
                                                            {ep.status || "Unknown"}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </PageShell>
    );
}

export default ClientDashboard;
